import React, { useEffect } from "react";
import "./App.css";
import { BRAND_PAGES, brandPath } from "./Brands.js";
// Same image the live site uses. Adjust the path if your repairman photo lives elsewhere.
import repairman from "./images/repairman.jpg";

/* ------------------------------------------------------------------
   SEO: title + meta description
   If your project already sets these in public/index.html or with
   react-helmet, put the same two strings there instead.
------------------------------------------------------------------- */
const PAGE_TITLE = "Appliance Repair Plainfield & Naperville, IL | AT Appliance";
const PAGE_DESCRIPTION =
  "Same-day appliance repair in Plainfield, Naperville & nearby suburbs. $79 diagnostic waived with repair. 30-day warranty. Call (331) 600-4935.";

/* ------------------------------------------------------------------
   FILL THESE IN — only what's true. Blank/false items stay hidden.
------------------------------------------------------------------- */
const OWNER_NAME = "";              // e.g. "Ali T." -> shows "Family-owned by Ali T., fixing appliances since 2016."
const LICENSED_AND_INSURED = false; // set to true ONLY if you carry liability insurance
const YEAR_FOUNDED = 2016;
const WARRANTY = "30-day parts and labor warranty";

/* Real photos: put the files in public/images/ and fill in the list.
   Empty entries are hidden, so the page works before you add any. */
const TEAM_PHOTOS = [
  { src: "", alt: "", caption: "" }, // e.g. { src: "/images/owner.jpg", alt: "Ali, owner of AT Appliance Repair, Plainfield IL", caption: "Ali, owner and lead technician" }
  { src: "", alt: "", caption: "" }, // e.g. technician
  { src: "", alt: "", caption: "" }, // e.g. "/images/van.jpg" — AT Appliance service van in Plainfield
  { src: "", alt: "", caption: "" }, // e.g. "/images/subzero-repair.jpg" — Sub-Zero compressor repair in Naperville
];

const PHONE_DISPLAY = "(331) 600-4935";
const PHONE_LINK = "tel:+13316004935";
const CDN = "https://uploads-ssl.webflow.com/622b5f32e69caa8dd1db5ef6/";

/* Local business structured data (helps Google connect the site to your map listing) */
const LOCAL_BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "HomeAndConstructionBusiness",
  name: "AT Appliance Repair and Maintenance",
  url: "https://atappliancemaster.com/",
  telephone: "+1-331-600-4935",
  image: "https://atappliancemaster.com/og-image.jpg",
  foundingDate: "2016",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Plainfield",
    addressRegion: "IL",
    postalCode: "60586",
    addressCountry: "US",
  },
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    opens: "08:00",
    closes: "21:00",
  },
  areaServed: ["Plainfield, IL", "Naperville, IL", "Aurora, IL", "Joliet, IL", "Oswego, IL", "Yorkville, IL", "Bolingbrook, IL", "Shorewood, IL", "Romeoville, IL"],
};

const SERVICES = [
  {
    title: "Refrigerator Repair",
    href: "/services/refrigerator-repair",
    icon: "622b8f088e29853f7b79cb2e_Group%204.png",
    text: "Fridge warm but freezer cold, water on the floor, or ice building up? We fix failed fans, defrost problems, leaks and ice makers on freestanding, French door, side-by-side and built-in refrigerators.",
  },
  {
    title: "Range & Stove Repair",
    href: "/services/stove-repair",
    icon: "622b8f081f7e1896d4d66c81_Group%2011.png",
    text: "Burner won't light, element won't heat or a cracked glass cooktop? We repair gas and electric ranges, stoves and cooktops, including pro-style models from Wolf, Viking and Bertazzoni.",
  },
  {
    title: "Washer Repair",
    href: "/services/washer-repair",
    icon: "622b8ea7c1f70541e0cc3e3d_washer-repair-service.png",
    text: "Washer won't drain, spin or start, or it shakes across the floor? We repair top-load, front-load and high-efficiency washers from every major brand.",
  },
  {
    title: "Oven Repair",
    href: "/services/oven-repair",
    icon: "622b8f0894f2b715bc0002e6_Group%2010.png",
    text: "Oven not heating, running hot or cold, or stuck in a self-clean cycle? We replace igniters, bake elements, sensors and door latches on gas, electric and wall ovens.",
  },
  {
    title: "Dishwasher Repair",
    href: "/services/dishwasher-repair",
    icon: "622b8f08f6538e9269ddf353_Group%203.png",
    text: "Leaking, not draining or leaving dishes dirty? We fix pumps, valves, spray arms and control boards on built-in dishwashers.",
  },
  {
    title: "Dryer Repair",
    href: "/services/dryer-repair",
    icon: "622b8f08e0c5eb1273fc2dab_Group%202.png",
    text: "Dryer not heating, taking two cycles to dry or making noise? We repair gas and electric dryers and check the vent so the problem doesn't come back.",
  },
  {
    title: "Wine Cooler Repair",
    href: "/services/wine-cooler-repair",
    icon: "622b8f088e29853f7b79cb2e_Group%204.png",
    text: "Wine cooler not holding temperature, frosting up or running nonstop? We repair built-in and freestanding wine coolers and beverage centers, including Sub-Zero and other high-end units.",
  },
  {
    title: "Commercial Appliance Repair",
    href: "/services/commercial-appliance-repair",
    icon: "622b8f081f7e1896d4d66c81_Group%2011.png",
    text: "Fast repair for restaurants, laundromats, hotels and apartment buildings, including commercial ranges from Garland and US Range.",
  },
];

/* Paste 3 real Google reviews here, word for word. Empty entries are hidden automatically. */
const REVIEWS = [
  { text: "", name: "", town: "" },
  { text: "", name: "", town: "" },
  { text: "", name: "", town: "" },
];
/* Paste your Google Business Profile review link here */
const GOOGLE_REVIEWS_URL = "https://business.google.com/v/at-appliance-repair-maintenance/017840338839401137673/e3eb/_";

const STATS = [
  { value: "Since 2016", label: "Family-owned and local" },
  { value: "500+", label: "Repairs completed" },
  { value: "5.0 ★", label: "53 Google reviews" },
  { value: "Same day", label: "Service in most cases" },
];

/* slug = the city page that exists on your site; null = no page yet (shown without a link).
   When you build an Aurora, Joliet, Oswego or Yorkville page, replace null with "aurora", "joliet", "oswego" or "yorkville". */
const COUNTIES = [
  {
    name: "Will County, IL",
    cities: [
      ["Plainfield (headquarters)", "plainfield"], ["Joliet", null], ["Naperville", "naperville"],
      ["Bolingbrook", "bolingbrook"], ["Shorewood", "shorewood"], ["Romeoville", "romeoville"],
      ["Lockport", "lockport"], ["Crest Hill", "crest-hill"], ["Homer Glen", "homer-glen"],
      ["Mokena", "mokena"], ["New Lenox", "new-lenox"],
    ],
  },
  {
    name: "Kendall & Kane County, IL",
    cities: [["Aurora", null], ["Oswego", null], ["Yorkville", null], ["St. Charles", null]],
  },
  {
    name: "DuPage County, IL",
    cities: [
      ["Naperville", "naperville"], ["Lisle", "lisle"], ["Downers Grove", "downers-grove"], ["Wheaton", "wheaton"],
      ["Darien", "darien"], ["Glen Ellyn", "glen-ellyn"], ["Hinsdale", "hinsdale"], ["Clarendon Hills", "clarendon-hill"],
      ["Oak Brook", "oak-brook"], ["Lombard", null], ["Villa Park", "villa-park"], ["Elmhurst", "elmhurst"],
    ],
  },
  {
    name: "Southwest Cook County, IL",
    cities: [
      ["Lemont", "lemont"], ["Burr Ridge", "burr-ridge"], ["Willow Springs", "willow-springs"],
      ["Western Springs", "western-springs"], ["Orland Park", "orland-park"], ["Palos Hills", "palos-hills"],
      ["Palos Heights", "palos-heights"], ["Hickory Hills", "hickory-hills"],
    ],
  },
];

/* Farther towns: kept as small links lower on the page (their city pages still get a link),
   so they don't blur the main local focus. */
const ALSO_SERVING = [
  ["Addison", "addison"], ["Glendale Heights", null], ["Bartlett", "bartlett"], ["Schaumburg", "schaumburg"],
  ["Hoffman Estates", null], ["Arlington Heights", "arlington-heights"], ["Palatine", null],
  ["Prospect Heights", "prospect-heights"], ["Barrington", "barrington"], ["Crestwood", "crestwood"],
  ["Elmwood Park", "elmwood-park"],
];

const cityLink = (label, slug) =>
  slug ? <a href={`/illinois-service-area/appliance-repair-${slug}-illinois`}>{label}</a> : label;

const BRANDS = {
  "Luxury and built-in": ["Sub-Zero", "Wolf", "Thermador", "Viking", "Miele", "Dacor", "Bertazzoni", "JennAir"],
  "Everyday brands": ["Whirlpool", "GE", "Samsung", "LG", "KitchenAid", "Bosch", "Frigidaire", "Electrolux", "Maytag"],
  Commercial: ["Garland", "US Range"],
};

const WHY_US = [
  {
    title: "Straightforward Pricing",
    icon: CDN + "62c41633d6de6ab6ed58768e_Straightforward-Pricing.png",
    text: "A $79 diagnostic covers the visit, inspection and written estimate. If you go ahead with the repair, the $79 is waived. Our labor and parts prices run about 10% below the industry-standard Appliance Blue Book rates.",
  },
  {
    title: "30-Day Parts and Labor Warranty",
    icon: CDN + "62c41794a52d51ec8e12bb25_Guaranteed-Workmanship.svg",
    text: "Every repair is covered for 30 days on both parts and labor. If the same problem comes back, we come back.",
  },
  {
    title: "Same-Day Service, 7 Days a Week",
    icon: null,
    text: "We're open Monday through Sunday, 8 a.m. to 9 p.m., and can reach most of Plainfield, Naperville, Aurora and Joliet the same day you call.",
  },
  {
    title: "No Mess Left Behind",
    icon: CDN + "62c41832120e4b36c842a069_Personal%20Attention.svg",
    text: "Our technicians wear shoe covers, protect your floors and clean up before leaving. We treat your home like our own.",
  },
];

const FAQS = [
  ["How much is a service call?", "The diagnostic is $79, or $99 for luxury brands such as Sub-Zero, Wolf, Thermador, Viking and Miele. It covers a visit from a qualified technician, a full inspection, a written estimate for parts and labor, and our honest advice on whether the repair is worth it."],
  ["Is the diagnostic fee waived if I go ahead with the repair?", "Yes. If you approve the repair, the diagnostic fee is waived and you pay only for the repair itself."],
  ["How do you set prices for parts and labor?", "We price from the Appliance Blue Book (Major Appliance Job Rate Guide), the industry standard, and aim to come in about 10% below those rates. You see the price before we start."],
  ["Do you offer same-day appliance repair in Plainfield and Naperville?", "In most cases, yes. We're open 7 days a week, 8 a.m. to 9 p.m. Call (331) 600-4935 to check today's openings."],
  ["Do I have to pay for the whole repair upfront?", "No. When a repair needs a part, you pay 50% when we order it and the other 50% once your appliance is fixed and tested. Read how our 50/50 payment plan works on our blog."],
  ["Is my repair guaranteed?", "Every repair includes a 30-day parts and labor warranty, and most luxury-brand repairs are covered for 90 days."],
  ["Is it worth repairing an older appliance?", "Usually, if the repair costs well under half the price of a comparable new unit and the appliance isn't near the end of its normal life. Our technician will tell you honestly if replacing makes more sense."],
  ["Do you repair Sub-Zero, Wolf and other luxury brands?", "Yes. We service built-in and high-end appliances, including Sub-Zero, Wolf, Thermador, Viking and Miele."],
];

export default function Home() {
  useEffect(() => {
    document.title = PAGE_TITLE;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", PAGE_DESCRIPTION);
  }, []);

  const reviews = REVIEWS.filter((r) => r.text.trim() !== "");
  const photos = TEAM_PHOTOS.filter((p) => p.src.trim() !== "");
  const yearsInBusiness = new Date().getFullYear() - YEAR_FOUNDED;
  const trustItems = [
    LICENSED_AND_INSURED ? "Licensed & insured" : null,
    `${yearsInBusiness} years in business (since ${YEAR_FOUNDED})`,
    WARRANTY,
  ].filter(Boolean);

  return (
    <main>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(LOCAL_BUSINESS_SCHEMA) }}
      />

      {/* ---------- HERO ---------- */}
      <section className="background-image">
        <div className="container">
          <h1 className="large-title">Appliance Repair in Plainfield, Naperville &amp; the Western Suburbs</h1>
          <p className="trust-line">{trustItems.join(" · ")}</p>
          <p>
            Appliance repair in Plainfield, Naperville, Aurora and Joliet from a family-owned local company.
            Since {YEAR_FOUNDED}, AT Appliance Repair and Maintenance has fixed refrigerators, washers, dryers,
            dishwashers, ovens and ranges for homes and businesses across Will, DuPage and Kendall counties.
            Same-day service is available in most cases, and you get a price before any work starts.
          </p>
          <p>
            <a className="phone" href={PHONE_LINK}>Call {PHONE_DISPLAY}</a>
            <a className="schedule-button" href="/contact">Schedule Service</a>
          </p>
          <p className="trust-line">$79 diagnostic, waived when you go ahead with the repair</p>
        </div>
      </section>

      {/* ---------- SERVICES ---------- */}
      <section className="repair-serives">
        <h2>Residential &amp; Commercial Appliance Repair Services</h2>
        <div className="grid-container">
          {SERVICES.map((s) => (
            <div className="grid-item" key={s.title}>
              <img src={CDN + s.icon} alt={s.title} width="64" height="64" loading="lazy" />
              <h3><a className="service-link" href={s.href}>{s.title}</a></h3>
              <p className="service-text">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- REVIEWS + STATS ---------- */}
      <section className="reviews">
        <h2 className="slider-title">What Plainfield and Naperville Customers Say</h2>
        <p className="slider-text">
          Rated 5.0 out of 5 from 53 Google reviews ·{" "}
          <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer">Read our Google reviews</a>
        </p>
        {reviews.length > 0 && (
          <div className="review-list">
            {reviews.map((r, i) => (
              <blockquote className="review" key={i}>
                <p>“{r.text}”</p>
                <footer>— {r.name}, {r.town}, IL</footer>
              </blockquote>
            ))}
          </div>
        )}
        <div className="trust-stats">
          {STATS.map((s) => (
            <div key={s.label}>
              <h3 className="trust-stats">{s.value}</h3>
              <p className="trust-stats">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- SERVICE AREA ---------- */}
      <section className="service-area city-contains">
        <h2 className="garantee-title">Appliance Repair in Plainfield, Aurora, Joliet &amp; Nearby Towns</h2>
        <p className="area-intro">
          Based in Plainfield, we serve homes within about 25 miles, from Aurora, Oswego and Yorkville to Joliet,
          Naperville and the southwest suburbs. Find your town below.
        </p>
        <div className="county-grid">
          {COUNTIES.map((c) => (
            <div key={c.name}>
              <h3 className="county-title">{c.name}</h3>
              <ul className="city-list">
                {c.cities.map(([label, slug]) => (
                  <li key={c.name + label}>{cityLink(label, slug)}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="also-serving">
          <strong>Also serving, call to check availability:</strong>{" "}
          {ALSO_SERVING.map(([label, slug], i) => (
            <React.Fragment key={label}>
              {i > 0 && " · "}
              {cityLink(label, slug)}
            </React.Fragment>
          ))}
        </p>
        <p className="area-intro">
          <a className="schedule-button" href="/serviceareas">See all service areas</a>
        </p>
      </section>

      {/* ---------- BRANDS ---------- */}
      <section className="brands-section">
        <h2 className="garantee-title">We Repair All Major Appliance Brands, Including Luxury Models</h2>
        <div className="brands-row">
          <img
            className="repairman"
            src={repairman}
            alt="AT Appliance technician repairing an appliance in a Plainfield, IL home"
            loading="lazy"
          />
          <div className="brands-text">
            <p>
              Our technicians service everyday brands and high-end built-in appliances alike. Whether it's a
              Whirlpool washer or a Sub-Zero refrigerator, we diagnose it on the first visit and give you a price
              before any repair starts.
            </p>
            {Object.entries(BRANDS).map(([group, list]) => (
              <div key={group}>
                <h3 className="brand-group">{group}</h3>
                <ul className="brands-home">
                  {list.map((b) => {
                    const page = BRAND_PAGES.find((p) => p.name === b);
                    return (
                      <li className="brand-item" key={b}>
                        {page ? <a className="service-link" href={brandPath(page.slug)}>{b}</a> : b}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- MEET THE TEAM ---------- */}
      <section className="about-us">
        <h2 className="garantee-title">Meet Your Local Appliance Repair Team</h2>
        <p className="about-bio">
          {OWNER_NAME
            ? `Family-owned by ${OWNER_NAME}, fixing appliances in Plainfield and the western suburbs since ${YEAR_FOUNDED}.`
            : `Family-owned and fixing appliances in Plainfield and the western suburbs since ${YEAR_FOUNDED}.`}{" "}
          When you call, you talk to the people who do the repair, not a call center.
        </p>
        {photos.length > 0 && (
          <div className="photo-grid">
            {photos.map((p) => (
              <figure key={p.src}>
                <img src={p.src} alt={p.alt} loading="lazy" />
                {p.caption && <figcaption>{p.caption}</figcaption>}
              </figure>
            ))}
          </div>
        )}
      </section>

      {/* ---------- WHY CHOOSE US ---------- */}
      <section className="why-us">
        <h2 className="garantee-title">Why Plainfield Homeowners Choose AT Appliance Repair</h2>
        <div className="why-grid">
          {WHY_US.map((w) => (
            <div className="why-item" key={w.title}>
              {w.icon && <img className="gar_icon" src={w.icon} alt="" width="40" height="40" loading="lazy" />}
              <h3>{w.title}</h3>
              <p>{w.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- FAQ ---------- */}
      <section className="faq">
        <h2 className="garantee-title">Appliance Repair FAQ</h2>
        {FAQS.map(([q, a]) => (
          <div className="faq-item" key={q}>
            <h3>{q}</h3>
            <p>{a}</p>
          </div>
        ))}
      </section>


      <a className="mobile-call" href={PHONE_LINK}>Call {PHONE_DISPLAY}</a>
    </main>
  );
}